import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileResponse,
  PdfResult,
  ResourceAttachmentDto,
} from 'src/api/FiretrolApiClient';
import { invalidateFor, MutationArgs } from 'src/api/queries/invalidationMap';

export type WorkflowModuleSaveOp = {
  [Op in keyof MutationArgs]: Op extends `${string}.save`
    ? MutationArgs[Op] extends { jobId: number }
      ? Op
      : never
    : never;
}[keyof MutationArgs];

export interface IWorkflowModuleMutationConfig<TDto, TRequest> {
  /** Lower-case noun used in the error-pipeline log context, e.g. "change". */
  label: string;
  saveOp: WorkflowModuleSaveOp;
  detailKey: (jobId: number, id: string) => QueryKey;
  getId: (dto: TDto) => string | undefined;
  save: (jobId: number, request: TRequest) => Promise<TDto>;
  attachFromDocument: (
    jobId: number,
    documentId: string,
  ) => Promise<ResourceAttachmentDto>;
  rotateExternalAccess: (jobId: number, id: string) => Promise<TDto>;
  uploadTempFile: (
    file: File,
    category: string | null,
  ) => Promise<ResourceAttachmentDto>;
  generatePdf: (jobId: number, id: string) => Promise<PdfResult>;
  previewPdf: (jobId: number, id: string) => Promise<FileResponse | null>;
  logPdf: (
    jobId: number,
    statuses: string[] | null,
    contactId: string | null,
  ) => Promise<FileResponse | null>;
}

export interface IWorkflowLogPdfArgs {
  jobId: number;
  statuses: string[] | null;
  contactId: string | null;
}

/**
 * Builds the write hooks shared by the five workflow record modules. Save and
 * rotate seed the detail cache with the server DTO before invalidating, so a
 * detail page reading the same key never renders the pre-save record.
 */
export function createModuleMutations<TDto, TRequest>(
  config: IWorkflowModuleMutationConfig<TDto, TRequest>,
) {
  const useSave = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ jobId, request }: { jobId: number; request: TRequest }) =>
        config.save(jobId, request),
      meta: { suppressErrorToast: true, errorContext: `${config.label} save` },
      onSuccess: (result, { jobId }) => {
        const id = config.getId(result);
        if (id) qc.setQueryData(config.detailKey(jobId, id), result);
      },
      onSettled: (_data, _err, { jobId }) =>
        invalidateFor(config.saveOp, qc, { jobId }),
    });
  };

  const useAttachFromDocument = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({
        jobId,
        documentId,
      }: {
        jobId: number;
        documentId: string;
      }) => config.attachFromDocument(jobId, documentId),
      meta: {
        successMessage: 'Document added. Press Save to keep it.',
        errorContext: `${config.label} attach from document`,
      },
      onSettled: (_data, _err, { jobId }) =>
        invalidateFor('workflowEntity.attachFromDocument', qc, { jobId }),
    });
  };

  const useRotateExternalAccess = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ jobId, id }: { jobId: number; id: string }) =>
        config.rotateExternalAccess(jobId, id),
      meta: {
        successMessage: 'Shared links revoked.',
        errorContext: `${config.label} revoke links`,
      },
      onSuccess: (result, { jobId, id }) =>
        qc.setQueryData(config.detailKey(jobId, id), result),
      onSettled: (_data, _err, { jobId }) =>
        invalidateFor(config.saveOp, qc, { jobId }),
    });
  };

  const useUploadTempFile = () =>
    useMutation({
      mutationFn: ({
        file,
        category,
      }: {
        file: File;
        category: string | null;
      }) => config.uploadTempFile(file, category),
      meta: { errorContext: `${config.label} attachment upload` },
    });

  const useGeneratePdf = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ jobId, id }: { jobId: number; id: string }) =>
        config.generatePdf(jobId, id),
      meta: {
        suppressErrorToast: true,
        errorContext: `${config.label} pdf generation`,
      },
      onSettled: (_data, _err, { jobId }) =>
        invalidateFor(config.saveOp, qc, { jobId }),
    });
  };

  const usePreviewPdf = () =>
    useMutation({
      mutationFn: ({ jobId, id }: { jobId: number; id: string }) =>
        config.previewPdf(jobId, id),
      meta: {
        suppressErrorToast: true,
        errorContext: `${config.label} pdf preview`,
      },
    });

  const useLogPdf = () =>
    useMutation({
      mutationFn: ({ jobId, statuses, contactId }: IWorkflowLogPdfArgs) =>
        config.logPdf(jobId, statuses, contactId),
      meta: {
        suppressErrorToast: true,
        errorContext: `${config.label} log pdf`,
      },
    });

  return {
    useSave,
    useAttachFromDocument,
    useUploadTempFile,
    useRotateExternalAccess,
    useGeneratePdf,
    usePreviewPdf,
    useLogPdf,
  };
}
